
import React from 'react';
import { Search, Filter, RotateCcw } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';

interface AdminGamesFiltersProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  categoryFilter: string;
  setCategoryFilter: (category: string) => void;
  statusFilter: string;
  setStatusFilter: (status: string) => void;
  categories: any[];
  totalGames: number;
  isLoading: boolean;
  setPage: (page: number) => void;
}

const AdminGamesFilters: React.FC<AdminGamesFiltersProps> = ({
  searchTerm,
  setSearchTerm,
  categoryFilter,
  setCategoryFilter,
  statusFilter,
  setStatusFilter,
  categories,
  totalGames,
  isLoading,
  setPage
}) => {
  const handleReset = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setStatusFilter('all');
    setPage(1);
  };

  const hasActiveFilters = searchTerm || categoryFilter !== 'all' || statusFilter !== 'all';

  return (
    <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
      <div className="flex flex-col sm:flex-row gap-3 flex-1">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search games..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            className="pl-10"
          />
        </div>

        <Select value={categoryFilter} onValueChange={(value) => {
          setCategoryFilter(value);
          setPage(1);
        }}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((category) => (
              <SelectItem key={category.id} value={category.slug}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={(value) => {
          setStatusFilter(value);
          setPage(1);
        }}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Games" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Games</SelectItem>
            <SelectItem value="visible">Visible Games</SelectItem>
            <SelectItem value="hidden">Hidden Games</SelectItem>
            <SelectItem value="featured">Featured Games</SelectItem>
            <SelectItem value="trending">Trending Games</SelectItem>
            <SelectItem value="new">New Games</SelectItem>
            <SelectItem value="unconfigured">Unconfigured Games</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-3">
        {hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            className="flex items-center gap-2"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </Button>
        )}
        
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <Badge variant="secondary" className="font-medium">
            {isLoading ? '...' : totalGames.toLocaleString()} games
          </Badge>
        </div>
      </div>
    </div>
  );
};

export default AdminGamesFilters;
